import { useCallback, useEffect, useState } from 'react';
import { RequireBot } from '../components/ChooseBot';
import { useBot } from '../contexts/BotContext';
import { hubClient, SESSION_EVENT_KINDS, type SessionEvent } from '../services/hubClient';

const inputCls = 'rounded border border-gray-300 px-3 py-2 text-sm';

const KIND_COLORS: Record<string, string> = {
  route: 'bg-blue-100 text-blue-800',
  'rag.search': 'bg-purple-100 text-purple-800',
  'llm.generate': 'bg-green-100 text-green-800',
  'tool.call': 'bg-amber-100 text-amber-800',
  clarification: 'bg-cyan-100 text-cyan-800',
  'form.start': 'bg-indigo-100 text-indigo-800',
  'form.complete': 'bg-emerald-100 text-emerald-800',
  'form.cancel': 'bg-gray-100 text-gray-700',
  'hook.deny': 'bg-red-100 text-red-800',
  error: 'bg-red-100 text-red-800',
};

function Row({ ev }: { ev: SessionEvent }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="cursor-pointer hover:bg-gray-50" onClick={() => setOpen((o) => !o)}>
        <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
          {new Date(ev.createdAt).toLocaleString()}
        </td>
        <td className="px-3 py-2">
          <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${KIND_COLORS[ev.kind] ?? 'bg-gray-100 text-gray-700'}`}>
            {ev.kind}
          </span>
        </td>
        <td className="px-3 py-2 font-mono text-xs text-gray-600">{ev.sessionId ?? '—'}</td>
        <td className="px-3 py-2 text-xs text-gray-500">{ev.channel ?? '—'}</td>
        <td className="px-3 py-2 text-gray-400">{open ? '▾' : '▸'}</td>
      </tr>
      {open && (
        <tr>
          <td colSpan={5} className="bg-gray-50 px-3 py-2">
            <pre className="max-h-80 overflow-auto text-xs text-gray-700">{JSON.stringify(ev.payload, null, 2)}</pre>
          </td>
        </tr>
      )}
    </>
  );
}

function SessionEventsPage() {
  const { system, botId } = useBot();
  const [kind, setKind] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [limit, setLimit] = useState(100);
  const [events, setEvents] = useState<SessionEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = sessionId.trim()
        ? await hubClient.getSessionEventsBySession(sessionId.trim(), limit)
        : await hubClient.listSessionEvents({ system, botId, kind: kind || undefined, limit });
      setEvents(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [system, botId, kind, sessionId, limit]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="h-full w-full overflow-y-auto bg-gray-50">
      <div className="mx-auto max-w-5xl px-6 py-8">
        <h1 className="text-2xl font-bold text-gray-900">Session events</h1>
        <p className="mb-6 text-gray-600">
          Append-only runtime trace ({system}/{botId}): every route decision, RAG search, LLM call, tool call, form step.
          Filter by kind, or drill into one session by id. (Runtime route/node-exec tables are write-only — not exposed.)
        </p>

        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div className="flex flex-col">
            <label className="mb-1 text-xs text-gray-500">Kind</label>
            <select value={kind} onChange={(e) => setKind(e.target.value)} className={inputCls} disabled={!!sessionId.trim()}>
              <option value="">all</option>
              {SESSION_EVENT_KINDS.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="mb-1 text-xs text-gray-500">Session id (drill-down)</label>
            <input value={sessionId} onChange={(e) => setSessionId(e.target.value)} placeholder="pageId_psid" className={`${inputCls} w-56 font-mono`} />
          </div>
          <div className="flex flex-col">
            <label className="mb-1 text-xs text-gray-500">Limit</label>
            <input type="number" value={limit} onChange={(e) => setLimit(Number(e.target.value) || 100)} className={`${inputCls} w-24`} />
          </div>
          <button onClick={load} disabled={loading} className="rounded bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-40">
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {error && <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}

        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Time</th>
                <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Kind</th>
                <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Session</th>
                <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Channel</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {events.map((ev) => (
                <Row key={ev.id} ev={ev} />
              ))}
              {!loading && events.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-sm text-gray-400">
                    No events.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default SessionEvents;

/** A1: renders only once a bot is chosen (no default bot). */
export function SessionEvents() {
  return (
    <RequireBot title="Session events">
      <SessionEventsPage />
    </RequireBot>
  );
}
